import Vue from 'vue';
import axios, { AxiosResponse, AxiosError } from 'axios';
import { Component } from 'vue-property-decorator';
import moment from 'moment';
import { EntityType } from '@/models/EntityType';
import Axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

export interface Lock {
    refreshURL: string;
    intervalNumber: NodeJS.Timeout;
    lockedBy: string;
    locked: Date;
}

@Component
export class LockHandler extends Vue {
    created(): void {
        console.log("%c Created LockHandler", "color: purple");
        window.addEventListener('focus', this.activatedEvent);
        window.addEventListener('blur', this.deactivatedEvent);
    }
    beforeDestroy(): void {
        console.log('%c Destroying LockHandler', 'color: magenta');
        window.removeEventListener('focus', this.activatedEvent);
        window.removeEventListener('blur', this.deactivatedEvent);
        this.clearAllLocks();
    }

    activatedEvent(): void {
        this.hasFocus = true;
    }

    deactivatedEvent(): void {
        this.hasFocus = false;
    }
    private locks: Lock[] = [];
    private hasFocus = true;
    // Bumped every time all locks are abandoned — via clearAllLocks (route change)
    // or beforeDestroy, both of which call clearAllLocks. An in-flight addLockWithURL
    // captures the generation it started in and releases instead of registering if the
    // generation has moved on by the time it resolves.
    private lockGeneration = 0;
    // Sequence for setExclusiveLock. Each call takes the next value; a call whose value
    // has been superseded by a later one by the time its async work resolves backs out
    // (releasing what it acquired), so only the most recently requested record stays locked.
    private exclusiveLockSeq = 0;

    public async clearAllLocks(): Promise<void> {
        // Invalidate any lock acquisition currently in-flight: when its POST resolves
        // it will see the bumped generation and release itself instead of registering
        // an interval this clear can't see (the lock isn't in this.locks yet).
        this.lockGeneration++;

        // Nothing to clear
        if (this.locks.length === 0) {
            console.log('No locks to clear');
            return;
        }

        console.log('%c Clearing all locks', 'color: magenta; font-weight: bold');

        // Detach the current locks synchronously before awaiting the release POSTs. A lock
        // registered by an in-flight addLockWithURL while the awaits below run then lands in
        // the fresh this.locks array instead of being clobbered into an untracked orphan.
        // The detach also makes concurrent calls safe without serializing them: each call
        // releases exactly the locks it detached, so a call overlapping a slow clear still
        // reaps anything registered since — no lock is ever skipped or released twice.
        const locksToClear = this.locks;
        this.locks = [];

        const unlockPromises = locksToClear.map(lock => {
            clearInterval(lock.intervalNumber);
            return axios.post(lock.refreshURL, true)
                .then(() => {
                    console.log(`Lock released: ${lock.refreshURL}`);
                })
                .catch(error => {
                    console.error(`Failed to release lock at ${lock.refreshURL}:`, error);
                });
        });

        // Wait for all unlocks to complete
        await Promise.all(unlockPromises);
    }

    /**
     * Acquire `url` as this component's sole lock, first releasing any other lock it holds
     * — whether already registered or still in flight. Use this for single-record edit
     * modals (on open, or when switching records) instead of storing a lock field and
     * releasing it by URL on close: that pattern races the close against the acquisition
     * POST, so a fast close leaves the heartbeat renewing the lease forever.
     *
     * Latest call wins: if another setExclusiveLock starts before this one finishes, this
     * one releases whatever it acquired and resolves to null. A rejected acquisition (e.g.
     * 418 locked-by-another-user) is re-thrown so callers can surface the locked message.
     */
    public async setExclusiveLock(url: string, refreshInterval: number): Promise<Lock | null> {
        const seq = ++this.exclusiveLockSeq;
        await this.clearAllLocks();
        if (seq !== this.exclusiveLockSeq) return null;
        let lock: Lock;
        try {
            lock = await this.addLockWithURL(url, refreshInterval);
        } catch (err) {
            // A server response (e.g. 418 locked-by-another-user) is a real outcome the
            // caller must surface, so re-throw it. Any other rejection means the acquisition
            // was invalidated in flight — the modal closed, or a newer setExclusiveLock bumped
            // the generation — in which case addLockWithURL has already released it. Treat that
            // as a benign no-op rather than letting it reach the caller as an error toast.
            if ((err as AxiosError)?.response) throw err;
            return null;
        }
        if (seq !== this.exclusiveLockSeq) {
            // The superseding call's clearAllLocks usually already released this lock; only
            // back it out ourselves if it's still registered, avoiding a duplicate release POST.
            if (this.locks.some(l => l.refreshURL === url)) {
                this.releaseLockAtURL(url);
            }
            return null;
        }
        return lock;
    }

    public addLockWithURL(url: string, refreshInterval: number): Promise<Lock> {
        const generation = this.lockGeneration;
        return this.refreshAtURL(url)
            .then(result => {
                if (this.lockGeneration !== generation) {
                    // All locks were abandoned — via a route change or via beforeDestroy —
                    // while this lock POST was in-flight, so this lock now has no owner.
                    // Release it immediately so it neither stays open on the server nor
                    // spawns an orphaned refresh interval. releaseLockAtURL also logs
                    // failures; its locks.filter/clearInterval are no-ops here since this
                    // lock was never pushed.
                    this.releaseLockAtURL(url);
                    throw new Error(`Lock at ${url} was invalidated before registration — released immediately`);
                }
                const intervalNumber = setInterval(
                    () => {
                        this.refreshAtURL(url)
                            //.then(result => {
                            //})
                            .catch(error => {
                                if (error.message == `We don't have focus`) {
                                    console.warn(`Window doesn't have focus, not refreshing the lock.`);
                                }
                                if (error.response && error.response.status == 418) {
                                    const lockURL = error.config.url;
                                    console.warn(`Had to release lock at ${lockURL}`);
                                    this.releaseLockAtURL(error.config.url);
                                }
                            });
                    },
                    refreshInterval);
                const lock: Lock = {
                    refreshURL: url,
                    intervalNumber: intervalNumber,
                    lockedBy: result.data.lockedBy,
                    locked: result.data.locked
                };
                this.locks.push(lock);
                return lock;
            })
            .catch(error => {
                throw error;
            });
    }

    async refreshAtURL(url: string): Promise<AxiosResponse<any>> {
        if (!this.hasFocus) {
            throw new Error(`We don't have focus`);
        }
        return axios.post(url, false)
            .then(response => {
                return response;
            })
            .catch(error => {
                throw error;
            });
    }

    releaseLockAtURL(url: string): void {
        this.locks = this.locks.filter((value: Lock) => {
            if (value.refreshURL == url) {
                clearInterval(value.intervalNumber);
            }
            return value.refreshURL != url;
        });

        // Release lock with proper error handling
        axios.post(url, true)
            .then(() => {
                console.log(`Lock released: ${url}`);
            })
            .catch((error) => {
                console.error(`Failed to release lock at ${url}:`, error);
            });
    }

    async showLockedMessage(entityType: EntityType, entityId: string, entityRecordName: string, lockData: any): Promise<void> {
        try {
            const lockedBy = lockData.lockedBy;
            const expires = lockData.expires;
            const localExpiration = moment.utc(expires).local().format("L LT");
            const entityName = EntityType[entityType];
            const requestRelease = await this.$root.$bvModal.msgBoxConfirm(`This ${entityName} is locked by ${lockedBy} until ${localExpiration}.`,
                {
                    title: `${entityName} Locked`,
                    cancelTitle: 'Ok',
                    okTitle: 'Request Release from User',
                    noFade: true,
                    centered: true
                });

            if (!requestRelease) return;

            try {
                await Axios.post("/notification/request-lock-release", {
                    entityType,
                    entityId,
                    entityName: entityRecordName,
                    username: lockedBy
                });
                this.$notification(NotificationOptions.success(`Sent lock release request to ${lockedBy}`));
            } catch (error) {
                console.warn(error);
                this.$notification(NotificationOptions.error("Error sending lock release request"));
            }

        } catch (err) {
            console.warn(err);
        }
    }
}
